package com.shreyasjoshi.components

import androidx.compose.runtime.*
import org.jetbrains.compose.web.dom.*
import com.shreyasjoshi.models.PortfolioData

@Composable
fun Navbar(
    isDarkMode: Boolean,
    onToggleTheme: () -> Unit,
    onOpenResumeModal: () -> Unit
) {
    var isMobileMenuOpen by remember { mutableStateOf(false) }

    Nav(
        attrs = {
            classNames(
                "portfolio-nav", "sticky", "top-0", "z-40", "w-full", "border-b", "backdrop-blur-md", "transition-colors",
                if (isDarkMode) "bg-[#050505]/90 border-white/10 text-white" else "bg-white/90 border-slate-200 text-slate-900"
            )
        }
    ) {
        Div(attrs = { classNames("max-w-6xl", "mx-auto", "px-4", "sm:px-6", "lg:px-8", "h-16", "flex", "items-center", "justify-between") }) {
            // Brand Logo & Name
            A(
                href = "#",
                attrs = { classNames("flex", "items-center", "gap-3", "group") }
            ) {
                Div(
                    attrs = {
                        classNames(
                            "w-8", "h-8", "rounded-sm", "bg-[#189AD3]", "flex", "items-center", "justify-center",
                            "text-white", "font-mono", "font-bold", "text-xs", "tracking-wider", "shadow-sm"
                        )
                    }
                ) {
                    Text("SJ")
                }
                Div {
                    Div(attrs = { classNames("font-bold", "text-sm", "tracking-tight") }) {
                        Text(PortfolioData.personalInfo.name)
                    }
                    Div(attrs = { classNames("text-[10px]", "font-mono", "text-[#189AD3]") }) {
                        Text("AAOS & AOSP")
                    }
                }
            }

            // Desktop Navigation Links
            Div(attrs = { classNames("hidden", "lg:flex", "items-center", "gap-6", "text-xs", "font-medium") }) {
                A(href = "#about", attrs = { classNames("hover:text-[#189AD3]", "transition-colors") }) { Text("About") }
                A(href = "#experience", attrs = { classNames("hover:text-[#189AD3]", "transition-colors") }) { Text("Experience") }
                A(href = "#projects", attrs = { classNames("hover:text-[#189AD3]", "transition-colors") }) { Text("Projects") }
                A(href = "#skills", attrs = { classNames("hover:text-[#189AD3]", "transition-colors") }) { Text("Skills") }
                A(href = "#knowledge", attrs = { classNames("hover:text-[#189AD3]", "transition-colors") }) { Text("Knowledge") }
                A(href = "#contact", attrs = { classNames("hover:text-[#189AD3]", "transition-colors") }) { Text("Contact") }

                // Resume Trigger Button
                Button(
                    attrs = {
                        classNames(
                            "px-3", "py-1.5", "rounded-sm", "border", "font-mono", "text-xs", "transition-all",
                            if (isDarkMode) "border-white/20 text-white/90 hover:border-[#189AD3] hover:text-[#189AD3] bg-white/5"
                            else "border-slate-300 text-slate-800 hover:border-[#189AD3] hover:text-[#189AD3] bg-slate-50"
                        )
                        onClick { onOpenResumeModal() }
                    }
                ) {
                    Text("Resume")
                }

                // Theme Switcher Button
                Button(
                    attrs = {
                        classNames(
                            "p-1.5", "rounded-sm", "border", "transition-colors",
                            if (isDarkMode) "border-white/10 text-white/70 hover:text-white bg-white/5"
                            else "border-slate-300 text-slate-700 hover:text-slate-900 bg-slate-100"
                        )
                        title(if (isDarkMode) "Switch to Light Mode" else "Switch to Dark Mode")
                        onClick { onToggleTheme() }
                    }
                ) {
                    Text(if (isDarkMode) "☀ Light" else "🌙 Dark")
                }
            }

            // Mobile Menu Toggle Button
            Div(attrs = { classNames("flex", "items-center", "gap-2", "lg:hidden") }) {
                Button(
                    attrs = {
                        classNames(
                            "p-1.5", "rounded-sm", "border", "text-xs",
                            if (isDarkMode) "border-white/10 text-white/70" else "border-slate-300 text-slate-700"
                        )
                        attr("aria-label", if (isDarkMode) "Switch to Light Mode" else "Switch to Dark Mode")
                        onClick { onToggleTheme() }
                    }
                ) {
                    Text(if (isDarkMode) "☀" else "🌙")
                }

                Button(
                    attrs = {
                        classNames(
                            "p-1.5", "rounded-sm", "border", "font-mono", "text-xs",
                            if (isDarkMode) "border-white/10 text-white" else "border-slate-300 text-slate-800"
                        )
                        attr("aria-label", if (isMobileMenuOpen) "Close navigation menu" else "Open navigation menu")
                        attr("aria-expanded", isMobileMenuOpen.toString())
                        attr("aria-controls", "mobile-navigation")
                        onClick { isMobileMenuOpen = !isMobileMenuOpen }
                    }
                ) {
                    Text(if (isMobileMenuOpen) "✕" else "☰")
                }
            }
        }

        // Mobile Menu Drawer
        if (isMobileMenuOpen) {
            Div(
                attrs = {
                    id("mobile-navigation")
                    classNames(
                        "mobile-menu", "lg:hidden", "px-4", "pt-2", "pb-4", "border-t", "space-y-3",
                        if (isDarkMode) "border-white/10 bg-[#0a0a0a]" else "border-slate-200 bg-slate-50"
                    )
                }
            ) {
                Div(attrs = { classNames("flex", "flex-col", "gap-3", "text-sm", "pt-2") }) {
                    A(href = "#about", attrs = { classNames("py-1"); onClick { isMobileMenuOpen = false } }) { Text("About") }
                    A(href = "#experience", attrs = { classNames("py-1"); onClick { isMobileMenuOpen = false } }) { Text("Experience") }
                    A(href = "#projects", attrs = { classNames("py-1"); onClick { isMobileMenuOpen = false } }) { Text("Projects") }
                    A(href = "#skills", attrs = { classNames("py-1"); onClick { isMobileMenuOpen = false } }) { Text("Skills") }
                    A(href = "#knowledge", attrs = { classNames("py-1"); onClick { isMobileMenuOpen = false } }) { Text("Knowledge Base") }
                    A(href = "#contact", attrs = { classNames("py-1"); onClick { isMobileMenuOpen = false } }) { Text("Contact") }

                    Button(
                        attrs = {
                            classNames("w-full", "py-2", "mt-2", "rounded-sm", "bg-[#189AD3]", "text-white", "font-mono", "text-xs", "font-bold")
                            onClick {
                                onOpenResumeModal()
                                isMobileMenuOpen = false
                            }
                        }
                    ) {
                        Text("View / Download Resume PDF")
                    }
                }
            }
        }
    }
}


